import styled, { createGlobalStyle } from 'styled-components';
import { Card, Button, Input, Select, Modal, Tag } from 'antd';

// Layout
export const Container = styled.div`
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Header
export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Title = styled.h1<{ $isDark: boolean }>`
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -1px;
  margin: 0;
  color: ${p => p.$isDark ? '#f7fafc' : '#1a202c'};

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

export const Subtitle = styled.p<{ $isDark: boolean }>`
  font-size: 14px;
  margin: 0;
  color: ${p => p.$isDark ? '#a0aec0' : '#718096'};
`;

export const AddButton = styled(Button)`
  background-color: #2d6cca !important;
  border-color: #2d6cca !important;
  color: #fff !important;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  height: auto;
  padding: 8px 20px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #2563c7 !important;
    border-color: #2563c7 !important;
    color: #fff !important;
  }

  &:focus {
    background-color: #2d6cca !important;
    border-color: #2d6cca !important;
    color: #fff !important;
  }
`;

// Search & Filters
export const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const StyledSearchInput = styled(Input)<{ $isDark: boolean }>`
  max-width: 400px;
  background: ${p => p.$isDark ? '#2d3748' : '#fff'} !important;
  border: 1px solid ${p => p.$isDark ? '#4a5568' : '#d9d9d9'} !important;
  border-radius: 8px;
  color: ${p => p.$isDark ? '#fff' : '#1a1a1a'} !important;
  font-size: 14px;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${p => p.$isDark ? '#718096' : '#bfbfbf'} !important;
  }

  &:hover {
    border-color: ${p => p.$isDark ? '#63b3ed' : '#40a9ff'} !important;
  }

  &:focus {
    border-color: ${p => p.$isDark ? '#63b3ed' : '#40a9ff'} !important;
    box-shadow: ${p => p.$isDark
      ? '0 0 0 2px rgba(99, 179, 237, 0.2)'
      : '0 0 0 2px rgba(24, 144, 255, 0.2)'} !important;
  }

  .ant-input-prefix {
    color: ${p => p.$isDark ? '#a0aec0' : '#8c8c8c'};
    margin-right: 8px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const StyledSelectFilter = styled(Select)<{ $isDark: boolean }>`
  min-width: 200px;

  .ant-select-selector {
    background: ${p => p.$isDark ? '#2d3748' : '#fff'} !important;
    border: 1px solid ${p => p.$isDark ? '#4a5568' : '#d9d9d9'} !important;
    border-radius: 8px !important;
    color: ${p => p.$isDark ? '#fff' : '#1a1a1a'} !important;
    font-size: 14px;

    .ant-select-selection-placeholder {
      color: ${p => p.$isDark ? '#718096' : '#bfbfbf'} !important;
    }

    .ant-select-selection-item {
      color: ${p => p.$isDark ? '#fff' : '#1a1a1a'} !important;
    }
  }

  &:hover .ant-select-selector {
    border-color: ${p => p.$isDark ? '#63b3ed' : '#40a9ff'} !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${p => p.$isDark ? '#63b3ed' : '#40a9ff'} !important;
    box-shadow: ${p => p.$isDark
      ? '0 0 0 2px rgba(99, 179, 237, 0.2)'
      : '0 0 0 2px rgba(24, 144, 255, 0.2)'} !important;
  }

  .ant-select-arrow {
    color: ${p => p.$isDark ? '#a0aec0' : '#8c8c8c'} !important;
  }

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

// Store Group Card
export const StoreCard = styled(Card)<{ $isDark: boolean }>`
  margin-bottom: 20px;
  border-radius: 12px;
  background: ${p => p.$isDark ? '#1a202c' : '#fff'};
  border: 1px solid ${p => p.$isDark ? '#2d3748' : '#e2e8f0'};
  box-shadow: ${p => p.$isDark
    ? '0 1px 3px rgba(0, 0, 0, 0.3)'
    : '0 1px 3px rgba(0, 0, 0, 0.08)'};
  transition: box-shadow 0.2s ease;
  overflow: hidden;

  &:hover {
    box-shadow: ${p => p.$isDark
      ? '0 4px 12px rgba(0, 0, 0, 0.4)'
      : '0 4px 12px rgba(0, 0, 0, 0.12)'};
  }

  .ant-card-head {
    background: ${p => p.$isDark ? '#2d3748' : '#f8fafc'};
    border-bottom: 1px solid ${p => p.$isDark ? '#4a5568' : '#e2e8f0'};
    padding: 16px 24px;
    min-height: unset;
  }

  .ant-card-body {
    padding: 24px;
    background: ${p => p.$isDark ? '#1a202c' : '#fff'};
  }
`;

export const StoreHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;
`;

export const StoreInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

export const StoreIconWrapper = styled.div<{ $isDark: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$isDark
    ? 'rgba(99, 179, 237, 0.15)'
    : 'rgba(49, 130, 206, 0.1)'};
  color: ${p => p.$isDark ? '#63b3ed' : '#3182ce'};
  font-size: 16px;
  flex-shrink: 0;
`;

export const StoreDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const StoreName = styled.h3<{ $isDark: boolean }>`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: ${p => p.$isDark ? '#f7fafc' : '#2d3748'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MerchantName = styled.p<{ $isDark: boolean }>`
  font-size: 13px;
  margin: 0;
  color: ${p => p.$isDark ? '#a0aec0' : '#718096'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StoreActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const DeviceCountTag = styled(Tag)<{ $isDark: boolean }>`
  background: ${p => p.$isDark ? 'rgba(99, 179, 237, 0.15)' : 'rgba(49, 130, 206, 0.08)'} !important;
  color: ${p => p.$isDark ? '#90cdf4' : '#2d6cca'} !important;
  border: 1px solid ${p => p.$isDark ? 'rgba(99, 179, 237, 0.3)' : 'rgba(49, 130, 206, 0.2)'} !important;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 10px;
  margin: 0;
`;

export const ToggleButton = styled(Button)<{ $isDark: boolean }>`
  color: ${p => p.$isDark ? '#a0aec0' : '#718096'} !important;
  border: none !important;
  background: transparent !important;
  border-radius: 8px;
  padding: 4px 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${p => p.$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'} !important;
    color: ${p => p.$isDark ? '#e2e8f0' : '#4a5568'} !important;
  }
`;

export const ExpandableContent = styled.div`
  padding-top: 8px;
`;

export const DevicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Device Card
export const DeviceCardWrapper = styled(Card)<{ $isDark: boolean }>`
  border-radius: 10px;
  background: ${p => p.$isDark ? '#2d3748' : '#f8fafc'};
  border: 1px solid ${p => p.$isDark ? '#4a5568' : '#e2e8f0'};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${p => p.$isDark ? '#63b3ed' : '#90cdf4'};
    box-shadow: ${p => p.$isDark
      ? '0 2px 8px rgba(99, 179, 237, 0.15)'
      : '0 2px 8px rgba(49, 130, 206, 0.1)'};
  }

  .ant-card-body {
    padding: 16px;
  }
`;

export const DeviceCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DeviceCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

export const DeviceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

export const DeviceName = styled.h4<{ $isDark: boolean }>`
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: ${p => p.$isDark ? '#f7fafc' : '#2d3748'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DeviceIdentifier = styled.p<{ $isDark: boolean }>`
  font-size: 13px;
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  color: ${p => p.$isDark ? '#a0aec0' : '#718096'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DeviceTypeBadge = styled(Tag)`
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  margin: 0;
  padding: 2px 10px;
`;

export const DeviceCardDivider = styled.div<{ $isDark: boolean }>`
  border-top: 1px solid ${p => p.$isDark ? '#4a5568' : '#e2e8f0'};
`;

export const DeviceMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DeviceMetaIcon = styled.span<{ $isDark: boolean }>`
  color: ${p => p.$isDark ? '#718096' : '#a0aec0'};
  font-size: 14px;
  display: flex;
  align-items: center;
`;

export const DeviceMetaText = styled.span<{ $isDark: boolean }>`
  font-size: 13px;
  color: ${p => p.$isDark ? '#cbd5e0' : '#4a5568'};
`;

export const AssignmentTag = styled(Tag)<{ $isAssigned: boolean }>`
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  margin: 0;
  padding: 2px 10px;
`;

export const DeviceCardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  padding-top: 4px;
`;

export const ActionButton = styled(Button)<{ $isDark: boolean }>`
  color: ${p => p.$isDark ? '#90cdf4' : '#3182ce'} !important;
  border: none !important;
  background: transparent !important;
  border-radius: 6px;
  padding: 4px 8px;
  transition: all 0.2s ease;

  &:hover {
    background: ${p => p.$isDark
      ? 'rgba(144, 205, 244, 0.1)'
      : 'rgba(49, 130, 206, 0.08)'} !important;
  }
`;

export const DangerActionButton = styled(Button)`
  color: #fc8181 !important;
  border: none !important;
  background: transparent !important;
  border-radius: 6px;
  padding: 4px 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(254, 178, 178, 0.1) !important;
    color: #f56565 !important;
  }
`;

// Empty State
export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  gap: 16px;
`;

export const EmptyStateIcon = styled.div<{ $isDark: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$isDark ? '#2d3748' : '#edf2f7'};
  color: ${p => p.$isDark ? '#4a5568' : '#cbd5e0'};
  font-size: 32px;
`;

export const EmptyStateTitle = styled.p<{ $isDark: boolean }>`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: ${p => p.$isDark ? '#a0aec0' : '#718096'};
`;

export const EmptyStateSubtext = styled.p<{ $isDark: boolean }>`
  font-size: 14px;
  margin: 0;
  color: ${p => p.$isDark ? '#718096' : '#a0aec0'};
  text-align: center;
  max-width: 320px;
`;

// Store Groups Container
export const StoreGroupsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Modal
export const StyledModal = styled(Modal)<{ $isDark: boolean }>`
  .ant-modal-content {
    background: ${p => p.$isDark ? '#1a202c' : '#fff'} !important;
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-modal-header {
    background: ${p => p.$isDark ? '#2d3748' : '#f8fafc'} !important;
    border-bottom: 1px solid ${p => p.$isDark ? '#4a5568' : '#e2e8f0'} !important;
    padding: 16px 24px;
  }

  .ant-modal-title {
    color: ${p => p.$isDark ? '#f7fafc' : '#1a202c'} !important;
    font-weight: 600;
  }

  .ant-modal-body {
    background: ${p => p.$isDark ? '#1a202c' : '#fff'} !important;
  }

  .ant-modal-footer {
    background: ${p => p.$isDark ? '#2d3748' : '#f8fafc'} !important;
    border-top: 1px solid ${p => p.$isDark ? '#4a5568' : '#e2e8f0'} !important;
    padding: 12px 24px;
  }

  .ant-modal-close {
    color: ${p => p.$isDark ? '#a0aec0' : '#718096'} !important;
  }
`;

export const ModalContainerGlobalStyle = createGlobalStyle<{ $isDark: boolean }>`
  .ant-modal-mask {
    backdrop-filter: blur(4px);
  }
`;

export const StyledFormItem = styled.div`
  margin-bottom: 20px;
`;

export const StyledFormLabel = styled.span<{ $isDark: boolean }>`
  color: ${p => p.$isDark ? '#e2e8f0' : '#2d3748'} !important;
  font-size: 14px;
  font-weight: 500;
`;

export const StyledInput = styled(Input)<{ $isDark: boolean }>`
  background: ${p => p.$isDark ? '#2d3748' : '#fff'} !important;
  border: 1px solid ${p => p.$isDark ? '#4a5568' : '#d9d9d9'} !important;
  border-radius: 8px;
  color: ${p => p.$isDark ? '#fff' : '#1a1a1a'} !important;
  font-size: 14px;
  padding: 8px 12px;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${p => p.$isDark ? '#718096' : '#bfbfbf'} !important;
  }

  &:hover {
    border-color: ${p => p.$isDark ? '#63b3ed' : '#40a9ff'} !important;
  }

  &:focus {
    border-color: ${p => p.$isDark ? '#63b3ed' : '#40a9ff'} !important;
    box-shadow: ${p => p.$isDark
      ? '0 0 0 2px rgba(99, 179, 237, 0.2)'
      : '0 0 0 2px rgba(24, 144, 255, 0.2)'} !important;
  }

  &:disabled {
    background: ${p => p.$isDark ? '#2d3748' : '#f5f5f5'} !important;
    color: ${p => p.$isDark ? '#718096' : '#bfbfbf'} !important;
    cursor: not-allowed;
  }
`;

export const StyledSelect = styled(Select)<{ $isDark: boolean }>`
  .ant-select-selector {
    background: ${p => p.$isDark ? '#2d3748' : '#fff'} !important;
    border: 1px solid ${p => p.$isDark ? '#4a5568' : '#d9d9d9'} !important;
    border-radius: 8px !important;
    color: ${p => p.$isDark ? '#fff' : '#1a1a1a'} !important;
    font-size: 14px;
    padding: 4px 12px;
    min-height: 40px;
    transition: all 0.2s ease;

    .ant-select-selection-placeholder {
      color: ${p => p.$isDark ? '#718096' : '#bfbfbf'} !important;
    }

    .ant-select-selection-item {
      color: ${p => p.$isDark ? '#fff' : '#1a1a1a'} !important;
    }
  }

  &:hover .ant-select-selector {
    border-color: ${p => p.$isDark ? '#63b3ed' : '#40a9ff'} !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${p => p.$isDark ? '#63b3ed' : '#40a9ff'} !important;
    box-shadow: ${p => p.$isDark
      ? '0 0 0 2px rgba(99, 179, 237, 0.2)'
      : '0 0 0 2px rgba(24, 144, 255, 0.2)'} !important;
  }

  .ant-select-arrow {
    color: ${p => p.$isDark ? '#a0aec0' : '#8c8c8c'} !important;
  }
`;

export const SaveButton = styled(Button)`
  background-color: #2d6cca !important;
  border-color: #2d6cca !important;
  color: #fff !important;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  height: auto;
  padding: 8px 20px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #2563c7 !important;
    border-color: #2563c7 !important;
    color: #fff !important;
  }

  &:disabled {
    opacity: 0.6;
  }
`;

export const CancelButton = styled(Button)`
  background-color: #ededed !important;
  border: 0 !important;
  border-radius: 8px;
  color: #121212 !important;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 20px;
  height: auto;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e0e0e0 !important;
    color: #121212 !important;
  }
`;
